import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { adminListPublishQueue } from "@/lib/blog-admin.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";

export const Route = createFileRoute("/admin/blog-calendar")({
  component: Calendar,
});

type Post = {
  id: string;
  slug: string;
  title: string;
  status: "draft" | "scheduled" | "published" | "archived";
  scheduled_at: string | null;
  article_role: "pillar" | "supporting" | null;
  cluster_id: string | null;
  category: string | null;
  hero_image_url: string | null;
  cover_url: string | null;
  source: string | null;
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function weekStart(offset: number) {
  const x = new Date();
  const day = x.getDay(); // Sun=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day + offset * 7);
  return x;
}

function fmt(d: Date) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function sourceBadge(s: string | null) {
  const v = (s ?? "").toLowerCase();
  if (v.includes("gsc")) return { label: "GSC", cls: "bg-amber-100 text-amber-900" };
  if (v.includes("pillar")) return { label: "Pillar", cls: "bg-primary/10 text-primary" };
  if (v.includes("supporting") || v.includes("cluster")) return { label: "Supporting", cls: "bg-secondary text-foreground" };
  if (v === "manual" || v === "") return { label: "Manual", cls: "bg-muted text-muted-foreground" };
  return { label: s ?? "—", cls: "bg-muted text-foreground" };
}

function statusColor(s: string) {
  if (s === "published") return "bg-green-100 text-green-800 border-green-300";
  if (s === "scheduled") return "bg-blue-100 text-blue-800 border-blue-300";
  if (s === "draft") return "bg-yellow-100 text-yellow-800 border-yellow-300";
  return "bg-gray-100 text-gray-700 border-gray-300";
}

function Calendar() {
  const list = useServerFn(adminListPublishQueue);
  const q = useQuery({ queryKey: ["admin-publish-queue"], queryFn: () => list() });
  const posts: Post[] = ((q.data as any)?.posts ?? []) as Post[];
  const clusters = ((q.data as any)?.clusters ?? []) as { id: string; name: string }[];
  const [offset, setOffset] = useState(0);
  const [preview, setPreview] = useState<Post | null>(null);

  const start = useMemo(() => weekStart(offset), [offset]);
  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [start],
  );

  const byDay = useMemo(() => {
    const map: Record<number, Post[]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    for (const p of posts) {
      // Drafts have no scheduled_at yet — place them on their planned day.
      const when = p.scheduled_at ? dayKey(new Date(p.scheduled_at)) : (p as any).planned_date ?? null;
      if (!when) continue;
      for (let i = 0; i < 7; i++) {
        if (when === dayKey(days[i])) {
          map[i].push(p);
          break;
        }
      }
    }
    // cluster post first, GSC post second
    for (const k of Object.keys(map)) {
      map[+k].sort((a, b) => {
        const rank = (p: Post) => (String(p.source ?? "").includes("gsc") ? 1 : 0);
        return rank(a) - rank(b);
      });
    }
    return map;
  }, [posts, days]);


  const clusterName = (id: string | null) => clusters.find((c) => c.id === id)?.name ?? "Unassigned";

  return (
    <div>
      <BlogSubNav />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Weekly content calendar</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sunday → Saturday. Pillar posts publish Monday and supporting posts Tue–Sun; GSC opportunity drafts land on Sunday.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setOffset((o) => o - 1)}>← Prev week</Button>
          <span className="text-sm font-semibold">
            {fmt(days[0])} – {fmt(days[6])}
          </span>
          <Button size="sm" variant="outline" onClick={() => setOffset(0)}>This week</Button>
          <Button size="sm" variant="outline" onClick={() => setOffset((o) => o + 1)}>Next week →</Button>
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-7">
          {days.map((d, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{DAYS[i]}</p>
                <p className="text-xs text-muted-foreground">{fmt(d)}</p>
              </div>
              <div className="space-y-2">
                {byDay[i].length === 0 ? (
                  <p className="text-xs italic text-muted-foreground">—</p>
                ) : (
                  byDay[i].map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPreview(p)}
                      className="w-full rounded-lg border border-border bg-background p-2 text-left text-xs transition hover:border-primary"
                    >
                      <div className="mb-1 flex flex-wrap items-center gap-1">
                        {p.article_role && (
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${p.article_role === "pillar" ? "bg-primary/10 text-primary" : "bg-secondary text-foreground"}`}>
                            {p.article_role}
                          </span>
                        )}
                        {(() => { const s = sourceBadge(p.source); return (
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${s.cls}`}>{s.label}</span>
                        ); })()}
                        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${statusColor(p.status)}`}>
                          {p.status}
                        </span>
                      </div>
                      <p className="line-clamp-2 font-semibold">{p.title}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground">{clusterName(p.cluster_id)}</p>
                    </button>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div
          role="dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-2xl border border-border bg-card p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  {preview.article_role ?? "post"} · {clusterName(preview.cluster_id)}
                </p>
                <h2 className="mt-1 font-display text-xl font-bold">{preview.title}</h2>
                <p className="mt-1 text-xs text-muted-foreground">/blog/{preview.slug}</p>
              </div>
              <button onClick={() => setPreview(null)} className="text-2xl leading-none text-muted-foreground">×</button>
            </div>
            {(preview.hero_image_url ?? preview.cover_url) && (
              <img
                src={(preview.hero_image_url ?? preview.cover_url) as string}
                alt=""
                className="mb-4 aspect-video w-full rounded-lg object-cover"
              />
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded border px-2 py-0.5 font-semibold ${statusColor(preview.status)}`}>{preview.status}</span>
              {preview.scheduled_at && <span className="text-muted-foreground">Scheduled: {new Date(preview.scheduled_at).toLocaleString()}</span>}
            </div>
            <div className="mt-4 flex gap-2">
              <Link to="/admin/posts/$id" params={{ id: preview.id }}>
                <Button size="sm">Edit draft</Button>
              </Link>
              {preview.status === "published" && (
                <a href={`/blog/${preview.slug}`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline">
                    <ExternalLink className="mr-1 h-3 w-3" /> View live
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
